import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { TONE_ICON, TONE_LABEL, TONE_STYLE, type Tone } from './tone';
import { cx } from './cx';
import { ToastContext, type ToastApi, type ToastOptions } from './toast-context';

interface ToastItem extends ToastOptions {
  id: string;
  tone: Tone;
}

const DEFAULT_MS = 6000;

/**
 * Toast notifications. Two always-present live regions (polite for info/success, assertive for
 * warning/errors) so every message is announced. Timers pause while the toast is hovered or has
 * focus (WCAG 2.2.1); errors stay until dismissed. At most `max` are shown, newest last.
 */
export function ToastProvider({
  children,
  max = 4,
  dismissLabel = 'Dismiss notification',
  regionLabel = 'Notifications',
}: {
  children: ReactNode;
  max?: number;
  dismissLabel?: string;
  regionLabel?: string;
}) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const seq = useRef(0);

  const dismiss = useCallback((id: string) => setToasts((ts) => ts.filter((t) => t.id !== id)), []);
  const show = useCallback(
    (t: ToastOptions) => {
      const id = `toast-${++seq.current}`;
      setToasts((ts) => [...ts, { ...t, id, tone: t.tone ?? 'info' }].slice(-max));
      return id;
    },
    [max],
  );
  const api = useMemo<ToastApi>(
    () => ({
      show,
      dismiss,
      success: (title, description) => show({ title, description, tone: 'success' }),
      info: (title, description) => show({ title, description, tone: 'info' }),
      warning: (title, description) => show({ title, description, tone: 'warning' }),
      error: (title, description) => show({ title, description, tone: 'danger', duration: null }),
    }),
    [show, dismiss],
  );

  const region = (assertive: boolean) => (
    <div
      role={assertive ? 'alert' : 'status'}
      aria-live={assertive ? 'assertive' : 'polite'}
      aria-atomic="false"
      className="flex flex-col gap-2"
    >
      {toasts
        .filter((t) => (t.tone === 'danger' || t.tone === 'warning') === assertive)
        .map((t) => (
          <Toast key={t.id} toast={t} onDismiss={() => dismiss(t.id)} dismissLabel={dismissLabel} />
        ))}
    </div>
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <section
        aria-label={regionLabel}
        data-inert-exempt=""
        className="pointer-events-none fixed inset-x-0 bottom-0 z-(--z-toast) flex flex-col items-stretch gap-2 p-3 sm:inset-x-auto sm:end-0 sm:w-96"
      >
        {region(false)}
        {region(true)}
      </section>
    </ToastContext.Provider>
  );
}

function Toast({
  toast,
  onDismiss,
  dismissLabel,
}: {
  toast: ToastItem;
  onDismiss: () => void;
  dismissLabel: string;
}) {
  const Icon = TONE_ICON[toast.tone];
  const duration = toast.duration === undefined ? DEFAULT_MS : toast.duration;
  const [paused, setPaused] = useState(false);
  const remaining = useRef(duration ?? 0);
  const onDismissRef = useRef(onDismiss);
  useEffect(() => {
    onDismissRef.current = onDismiss;
  }, [onDismiss]);

  useEffect(() => {
    if (duration === null || paused) return;
    const started = Date.now();
    const timer = setTimeout(() => onDismissRef.current(), remaining.current);
    return () => {
      clearTimeout(timer);
      remaining.current -= Date.now() - started;
    };
  }, [duration, paused]);

  return (
    <div
      data-tone={toast.tone}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      className={cx(
        'pointer-events-auto flex gap-3 border px-3 py-2.5 shadow-toast',
        TONE_STYLE[toast.tone],
      )}
    >
      <Icon aria-hidden="true" className="mt-0.5 size-5 shrink-0" />
      <div className="min-w-0 flex-1 text-sm">
        <span className="sr-only">{TONE_LABEL[toast.tone]}: </span>
        <p className="font-semibold">{toast.title}</p>
        {toast.description && <p className="mt-0.5">{toast.description}</p>}
        {toast.action && (
          <button
            type="button"
            className="mt-1 font-semibold underline underline-offset-2"
            onClick={() => {
              toast.action?.onClick();
              onDismiss();
            }}
          >
            {toast.action.label}
          </button>
        )}
      </div>
      <button
        type="button"
        aria-label={dismissLabel}
        onClick={onDismiss}
        className="-me-1 flex size-7 shrink-0 items-center justify-center opacity-80 hover:opacity-100"
      >
        <X aria-hidden="true" className="size-4" />
      </button>
    </div>
  );
}
